// The file viewers `ArtifactRef` dispatches to by filename suffix. Each entry
// lazy-loads its library; the suffixes must also be in the conversations
// preview service's `_INLINE_FETCH_SUFFIXES`, which gives these previews the
// presigned `content.url` the viewers read.

import { defineFileViewer, type FileViewer } from "./defineFileViewer";
import { GENOME_TRACK_SUFFIXES } from "./genomeTracks";

const MB = 1024 * 1024;
// For viewers whose content sets its own height: room for the loading state,
// and a scroll past the cap.
const CONTENT_HEIGHT = "min-h-[240px] max-h-[560px] overflow-auto";

export const genomeTrackViewer = defineFileViewer({
  suffixes: GENOME_TRACK_SUFFIXES,
  // igv.js reads the URL itself; the track viewer enforces its own cap.
  maxBytes: 100 * MB,
  loadingLabel: "Loading genome tracks...",
  heightClass: "min-h-[420px]",
  load: () => import("./genomeTrackViewer"),
});

export const karyotypeViewer = defineFileViewer({
  suffixes: [".ideogram.json"],
  maxBytes: 5 * MB,
  loadingLabel: "Loading karyotype...",
  heightClass: "h-[600px]",
  load: () => import("./ideogramViewer"),
});

export const moleculeViewer = defineFileViewer({
  suffixes: [".mol", ".sdf", ".smi"],
  maxBytes: 20 * MB,
  loadingLabel: "Loading molecules...",
  heightClass: CONTENT_HEIGHT,
  load: () => import("./rdkitViewer"),
});

export const networkViewer = defineFileViewer({
  suffixes: [".cyjs", ".sif"],
  maxBytes: 20 * MB,
  loadingLabel: "Laying out network...",
  load: () => import("./cytoscapeViewer"),
});

export const treeViewer = defineFileViewer({
  suffixes: [".nwk", ".newick", ".nhx"],
  maxBytes: 10 * MB,
  loadingLabel: "Loading tree...",
  heightClass: CONTENT_HEIGHT,
  load: () => import("./phylotreeViewer"),
});

export const sequenceMapViewer = defineFileViewer({
  suffixes: [".gb", ".gbk", ".genbank"],
  maxBytes: 20 * MB,
  loadingLabel: "Loading sequence map...",
  heightClass: "h-[480px]",
  load: () => import("./seqvizViewer"),
});

export const vegaLiteViewer = defineFileViewer({
  suffixes: [".vl.json"],
  maxBytes: 10 * MB,
  loadingLabel: "Rendering chart...",
  heightClass: CONTENT_HEIGHT,
  load: () => import("./vegaLiteViewer"),
});

export const volumeViewer = defineFileViewer({
  suffixes: [".nii", ".nii.gz"],
  maxBytes: 256 * MB,
  loadingLabel: "Loading volume...",
  load: () => import("./niivueViewer"),
});

export const embeddingViewer = defineFileViewer({
  suffixes: [".embedding.parquet"],
  maxBytes: 200 * MB,
  loadingLabel: "Loading embedding...",
  load: () => import("./embeddingViewer"),
});

export const spatialMapViewer = defineFileViewer({
  suffixes: [".spatial.parquet"],
  // The conversation service's artifact cap.
  maxBytes: 50 * MB,
  loadingLabel: "Loading spatial map...",
  heightClass: "h-[560px]",
  load: () => import("./spatialMapViewer"),
});

export const omeImageViewer = defineFileViewer({
  suffixes: [".ome.tif", ".ome.tiff"],
  // Viv range-reads the tiles in view; the file is never downloaded whole.
  maxBytes: 50 * MB,
  loadingLabel: "Loading image...",
  heightClass: "h-[560px]",
  load: () => import("./omeImageViewer"),
});

export const FILE_VIEWERS: readonly FileViewer[] = [
  genomeTrackViewer,
  karyotypeViewer,
  moleculeViewer,
  networkViewer,
  treeViewer,
  sequenceMapViewer,
  vegaLiteViewer,
  volumeViewer,
  embeddingViewer,
  spatialMapViewer,
  omeImageViewer,
];

export function fileViewerFor(preview: Parameters<FileViewer["matches"]>[0]): FileViewer | null {
  return FILE_VIEWERS.find((viewer) => viewer.matches(preview)) ?? null;
}
